/**
 * Reports: the record, read four ways, and every view exportable to the share sheet.
 * Assets (tax year) · Clients & pots (history, write-offs) · Stock health · Month.
 * Read-only except write-offs, which are recorded facts with Undo.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Redo2, Share, TriangleAlert, Undo2 } from "lucide-react";
import {
  addMonths,
  assetRegister,
  clientHistory,
  headroom,
  idleStock,
  monthSummary,
  potStory,
  taxYearOf,
  type Assessor,
  type CalendarDate,
  type Catalog,
  type History,
  type WriteOffReason,
} from "@hitl/rotations-domain";
import type { EventLog } from "../state/eventLog";
import { undoCommand, writeOffCommand, type EventDraft } from "../state/session";
import { exportFileName, rands, toCsv } from "../state/exportCsv";
import { shareFile } from "../state/share";
import { loadPhoto, savePhoto } from "../state/photoStore";
import { formatDay, WRITE_OFF_LABEL } from "../scan/copy";
import { WriteOffSheet } from "./WriteOffSheet";

type Tab = "assets" | "history" | "stock" | "month";
const TABS: { id: Tab; label: string }[] = [
  { id: "assets", label: "Assets" },
  { id: "history", label: "Clients & pots" },
  { id: "stock", label: "Stock" },
  { id: "month", label: "Month" },
];

export interface ReportsFixture {
  readonly tab?: Tab;
  readonly potId?: string;
  readonly clientId?: string;
  readonly writeOff?: boolean;
  /** Show an info toast on open (review of the export confirmation). */
  readonly toast?: string;
  /** Focus the photo button on open (review of its focus ring). */
  readonly focusPhoto?: boolean;
}

const money = (cents: number) => `R ${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const potLabel = (catalog: Catalog, potId: string) => {
  const a = catalog.pot(potId)?.attributes;
  const text = a ? [a.size?.[0], a.colour?.join(" and ")].filter(Boolean).join(" · ") : "Not in your pot list";
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const where = (catalog: Catalog, clientId: string, slotId: string) =>
  `${catalog.client(clientId)?.name ?? clientId}, ${catalog.slot(slotId).label}`;

export function Reports(props: {
  catalog: Catalog;
  history: History;
  assessor: Assessor;
  log: EventLog;
  today: CalendarDate;
  business: string;
  onBack: () => void;
  fixture?: ReportsFixture;
}) {
  const { catalog, history, assessor, log, today, business, fixture } = props;
  const [tab, setTab] = useState<Tab>(fixture?.tab ?? "assets");
  const [year, setYear] = useState(() => taxYearOf(today));
  const [month, setMonth] = useState(today.slice(0, 7));
  const [pick, setPick] = useState<{ kind: "client"; id: string } | { kind: "pot"; id: string } | null>(
    fixture?.potId ? { kind: "pot", id: fixture.potId } : fixture?.clientId ? { kind: "client", id: fixture.clientId } : null,
  );
  const [historyMode, setHistoryMode] = useState<"clients" | "pots">(fixture?.potId ? "pots" : "clients");
  const [writingOff, setWritingOff] = useState<string | null>(fixture?.writeOff && fixture.potId ? fixture.potId : null);
  // One toast for what just happened: a write-off (with Undo, then Redo) or an export outcome.
  type WriteOffToast = { kind: "writeoff"; id: number; pot: string; text: string; drafts: EventDraft[]; ids: string[]; action: "undo" | "redo" };
  type InfoToast = { kind: "info"; id: number; text: string };
  const [toast, setToast] = useState<WriteOffToast | InfoToast | null>(fixture?.toast ? { kind: "info", id: 1, text: fixture.toast } : null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((c) => (c?.id === toast.id ? null : c)), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const exportCsv = async (name: string, csv: string) => {
    const outcome = await shareFile(exportFileName(business, name), csv);
    if (outcome !== "cancelled") setToast({ kind: "info", id: Date.now(), text: `${name.charAt(0).toUpperCase()}${name.slice(1)} ${outcome === "shared" ? "shared" : "saved to your downloads"}` });
  };

  const writeOff = async (potId: string, reason: WriteOffReason, note: string, photo: Blob | null) => {
    const photoId = photo ? await savePhoto(photo) : null;
    const drafts = writeOffCommand(potId, today, reason, { note, ...(photoId ? { photoId } : {}) });
    const created = log.append(drafts);
    setWritingOff(null);
    setToast({ kind: "writeoff", id: Date.now(), pot: potId, text: ` written off: ${WRITE_OFF_LABEL[reason].toLowerCase()}`, drafts, ids: created.map((e) => e.id), action: "undo" });
  };

  // Undo records a correction; Redo records the write-off again. Never a no-op.
  const pressToast = () => {
    if (toast?.kind !== "writeoff") return;
    if (toast.action === "undo") {
      log.append(undoCommand(toast.ids));
      setToast({ ...toast, id: Date.now(), action: "redo" });
    } else {
      const created = log.append(toast.drafts);
      setToast({ ...toast, id: Date.now(), ids: created.map((e) => e.id), action: "undo" });
    }
  };

  return (
    <main className="reports">
      {toast?.kind === "info" && (
        <div className="toast" role="status" key={toast.id}>
          <span>{toast.text}</span>
        </div>
      )}
      {toast?.kind === "writeoff" && (
        <div className="toast" role="status" key={toast.id}>
          <span>
            {toast.action === "redo" && "Undone: "}
            <span className="inline-pot">{toast.pot}</span>
            {toast.text}
          </span>
          <button className="toast__undo" onClick={pressToast}>
            {toast.action === "undo" ? <Undo2 aria-hidden size={18} strokeWidth={2.5} /> : <Redo2 aria-hidden size={18} strokeWidth={2.5} />}
            {toast.action === "undo" ? "Undo" : "Redo"}
          </button>
        </div>
      )}
      <header className="plan__top">
        <button className="plan__back" onClick={pick ? () => setPick(null) : props.onBack}>
          <ChevronLeft aria-hidden size={22} />
          {pick ? (pick.kind === "pot" ? "Pots" : "Clients") : "Home"}
        </button>
      </header>
      <h1 className="reports__title">Reports</h1>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`tabs__tab${tab === t.id ? " is-on" : ""}`}
            onClick={() => {
              setTab(t.id);
              setPick(null);
              setToast((c) => (c?.kind === "info" ? null : c));
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "assets" && <AssetsTab catalog={catalog} history={history} year={year} setYear={setYear} onPot={(id) => { setTab("history"); setHistoryMode("pots"); setPick({ kind: "pot", id }); }} onExport={exportCsv} />}
      {tab === "history" && !pick && (
        <HistoryList catalog={catalog} history={history} mode={historyMode} setMode={setHistoryMode} onPick={setPick} />
      )}
      {tab === "history" && pick?.kind === "client" && <ClientView catalog={catalog} history={history} clientId={pick.id} today={today} onPot={(id) => setPick({ kind: "pot", id })} onExport={exportCsv} />}
      {tab === "history" && pick?.kind === "pot" && (
        <PotView catalog={catalog} history={history} potId={pick.id} today={today} onWriteOff={() => setWritingOff(pick.id)} onExport={exportCsv} />
      )}
      {tab === "stock" && <StockTab catalog={catalog} history={history} assessor={assessor} today={today} onExport={exportCsv} />}
      {tab === "month" && <MonthTab catalog={catalog} history={history} month={month} setMonth={setMonth} onExport={exportCsv} />}

      {writingOff && (
        <WriteOffSheet
          potId={writingOff}
          {...(fixture?.focusPhoto ? { focusPhoto: true } : {})}
          {...(history.whereIs(writingOff) ? { where: where(catalog, history.whereIs(writingOff)!.clientId, history.whereIs(writingOff)!.slotId) } : {})}
          onSubmit={(reason, note, photo) => void writeOff(writingOff, reason, note, photo)}
          onClose={() => setWritingOff(null)}
        />
      )}
    </main>
  );
}

function ExportButton(props: { onClick: () => void; label?: string }) {
  return (
    <button className="btn btn--ghost btn--block reports__export" onClick={props.onClick}>
      <Share aria-hidden size={18} /> {props.label ?? "Export (Excel / CSV)"}
    </button>
  );
}

/** A count in the planner's readout form: unboxed display numeral, a zero greyed, amber only for a real caution. */
function Figure(props: { value: string | number; label: string; tone?: "check" | undefined; zero?: boolean }) {
  const zero = props.zero ?? props.value === 0;
  return (
    <span className={`figure${zero ? " figure--zero" : props.tone ? ` figure--${props.tone}` : ""}`}>
      <b>{props.value}</b> {props.label}
    </span>
  );
}

/* ── Assets ───────────────────────────────────────────────────────────────── */

function AssetsTab(props: {
  catalog: Catalog;
  history: History;
  year: ReturnType<typeof taxYearOf>;
  setYear: (y: ReturnType<typeof taxYearOf>) => void;
  onPot: (id: string) => void;
  onExport: (name: string, csv: string) => void;
}) {
  const { catalog, history, year } = props;
  const reg = useMemo(() => assetRegister(catalog, history, year), [catalog, history, year]);
  const statusText = (r: (typeof reg.rows)[number]) =>
    r.status === "written-off" && r.writeOff
      ? `Written off ${formatDay(r.writeOff.on)} · ${WRITE_OFF_LABEL[r.writeOff.reason]}`
      : r.status === "placed" && r.placedAt
        ? `At ${where(catalog, r.placedAt.clientId, r.placedAt.slotId)}`
        : r.status === "retired"
          ? "Retired"
          : "In stock";
  const csv = () =>
    toCsv(
      [
        { header: "Pot", value: (r: (typeof reg.rows)[number]) => r.pot.id },
        { header: "Description", value: (r) => potLabel(catalog, r.pot.id) },
        { header: "Status", value: (r) => r.status },
        { header: "Where", value: (r) => (r.placedAt ? where(catalog, r.placedAt.clientId, r.placedAt.slotId) : "") },
        { header: "Bought on", value: (r) => r.pot.asset?.boughtOn ?? "" },
        { header: "Cost (R)", value: (r) => rands(r.pot.asset?.costCents) },
        { header: "Supplier", value: (r) => r.pot.asset?.supplier ?? "" },
        { header: "Bought in tax year", value: (r) => (r.boughtThisYear ? "Yes" : "") },
        { header: "Written off on", value: (r) => r.writeOff?.on ?? "" },
        { header: "Write-off reason", value: (r) => (r.writeOff ? WRITE_OFF_LABEL[r.writeOff.reason] : "") },
        { header: "Write-off note", value: (r) => r.writeOff?.note ?? "" },
        { header: "Written off in tax year", value: (r) => (r.writtenOffThisYear ? "Yes" : "") },
      ],
      reg.rows,
    );
  const shift = (n: number) => props.setYear(taxYearOf(addMonths(year.from, 12 * n)));

  return (
    <section className="rtab" aria-label="Assets">
      <div className="switcher">
        <button aria-label="Previous tax year" onClick={() => shift(-1)}>
          <ChevronLeft aria-hidden size={20} />
        </button>
        <span>
          Tax year <b>{year.label}</b>
          <small>
            {formatDay(year.from)} – {formatDay(year.to)}
          </small>
        </span>
        <button aria-label="Next tax year" onClick={() => shift(1)}>
          <ChevronRight aria-hidden size={20} />
        </button>
      </div>
      <div className="figures">
        <Figure value={reg.totals.inCirculation} label="in circulation" />
        <Figure value={money(reg.totals.boughtThisYear.costCents)} label={`bought (${reg.totals.boughtThisYear.count})`} zero={reg.totals.boughtThisYear.costCents === 0} />
        <Figure value={money(reg.totals.writtenOffThisYear.costCents)} label={`written off (${reg.totals.writtenOffThisYear.count})`} zero={reg.totals.writtenOffThisYear.costCents === 0} />
      </div>
      {reg.totals.missingCost > 0 && (
        <p className="alert">
          <TriangleAlert aria-hidden size={16} />
          {reg.totals.missingCost} pots have no cost recorded. Your accountant will need these.
        </p>
      )}
      <ExportButton onClick={() => props.onExport(`assets ${year.label}`, csv())} />
      <ul className="rlist">
        {reg.rows.map((r) => (
          <li key={r.pot.id}>
            <button className={`rrow${r.status === "written-off" ? " is-off" : ""}`} onClick={() => props.onPot(r.pot.id)}>
              <span className="rrow__pot">{r.pot.id}</span>
              <span className="rrow__main">
                {potLabel(catalog, r.pot.id)}
                <small>{statusText(r)}</small>
              </span>
              <span className="rrow__side">{r.pot.asset?.costCents !== undefined ? money(r.pot.asset.costCents) : <em>No cost</em>}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Clients & pots ──────────────────────────────────────────────────────── */

function HistoryList(props: {
  catalog: Catalog;
  history: History;
  mode: "clients" | "pots";
  setMode: (m: "clients" | "pots") => void;
  onPick: (p: { kind: "client"; id: string } | { kind: "pot"; id: string }) => void;
}) {
  const { catalog, history, mode } = props;
  const [query, setQuery] = useState("");
  const clients = [...new Set(catalog.slots.map((s) => s.clientId))]
    .map((id) => ({ id, name: catalog.client(id)?.name ?? id }))
    .filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const pots = catalog.pots.filter((p) => p.id.toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="rtab" aria-label="Clients and pots">
      <div className="seg2" role="tablist">
        <button role="tab" aria-selected={mode === "clients"} className={mode === "clients" ? "is-on" : ""} onClick={() => props.setMode("clients")}>
          Clients
        </button>
        <button role="tab" aria-selected={mode === "pots"} className={mode === "pots" ? "is-on" : ""} onClick={() => props.setMode("pots")}>
          Pots
        </button>
      </div>
      <label className="field">
        <span className="field__label">Search</span>
        <input className="field__input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={mode === "clients" ? "Client name" : "Pot number"} />
      </label>
      <ul className="rlist">
        {mode === "clients"
          ? clients.map((c) => (
              <li key={c.id}>
                <button className="rrow" onClick={() => props.onPick({ kind: "client", id: c.id })}>
                  <span className="rrow__main rrow__main--wide">
                    {c.name}
                    <small>{history.placements.filter((p) => p.clientId === c.id).length} placements on record</small>
                  </span>
                  <ChevronRight aria-hidden size={18} />
                </button>
              </li>
            ))
          : pots.map((p) => (
              <li key={p.id}>
                <button className={`rrow${history.writtenOff(p.id) ? " is-off" : ""}`} onClick={() => props.onPick({ kind: "pot", id: p.id })}>
                  <span className="rrow__pot">{p.id}</span>
                  <span className="rrow__main">
                    {potLabel(catalog, p.id)}
                    <small>{history.writtenOff(p.id) ? "Written off" : history.whereIs(p.id) ? "At a client" : "In stock"}</small>
                  </span>
                  <ChevronRight aria-hidden size={18} />
                </button>
              </li>
            ))}
      </ul>
    </section>
  );
}

function ClientView(props: { catalog: Catalog; history: History; clientId: string; today: CalendarDate; onPot: (id: string) => void; onExport: (n: string, csv: string) => void }) {
  const { catalog, history, clientId, today } = props;
  const c = clientHistory(history, clientId, today);
  const name = catalog.client(clientId)?.name ?? clientId;
  const csv = () =>
    toCsv(
      [
        { header: "Pot", value: (s: (typeof c.stays)[number]) => s.potId },
        { header: "Spot", value: (s) => catalog.slot(s.slotId).label },
        { header: "Placed on", value: (s) => s.placedOn },
        { header: "Collected on", value: (s) => s.collectedOn ?? "Still there" },
        { header: "Days", value: (s) => s.days },
        { header: "Override reason", value: (s) => s.override?.reason ?? "" },
      ],
      c.stays,
    );
  return (
    <section className="rtab" aria-label={name}>
      <h2 className="rtab__h">{name}</h2>
      <div className="figures">
        <Figure value={c.stays.length} label="placements" />
        <Figure value={c.distinctPots} label="different pots" />
        <Figure value={c.refusals.length} label="refusals" />
      </div>
      <ExportButton onClick={() => props.onExport(`${name} history`, csv())} />
      <h3 className="rtab__sub">Arrangements, newest first</h3>
      <ul className="rlist">
        {c.stays.map((s) => (
          <li key={s.eventId}>
            <button className="rrow" onClick={() => props.onPot(s.potId)}>
              <span className="rrow__pot">{s.potId}</span>
              <span className="rrow__main">
                {catalog.slot(s.slotId).label}
                <small>
                  {formatDay(s.placedOn)} – {s.collectedOn ? formatDay(s.collectedOn) : "still there"}
                </small>
              </span>
              <span className="rrow__side">{s.days} days</span>
            </button>
          </li>
        ))}
      </ul>
      {c.refusals.length > 0 && (
        <>
          <h3 className="rtab__sub">Refused</h3>
          <ul className="rlist">
            {c.refusals.map((r) => (
              <li key={r.eventId} className="rrow rrow--static">
                <span className="rrow__pot">{r.potId}</span>
                <span className="rrow__main">
                  {r.reason ?? "No reason given"}
                  <small>{formatDay(r.on)}</small>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function PotView(props: { catalog: Catalog; history: History; potId: string; today: CalendarDate; onWriteOff: () => void; onExport: (n: string, csv: string) => void }) {
  const { catalog, history, potId, today } = props;
  const story = potStory(history, potId, today);
  const pot = catalog.pot(potId);
  const [photo, setPhoto] = useState<string | null>(null);
  const photoId = story.writeOff?.photoId;
  useEffect(() => {
    let url: string | null = null;
    if (photoId) void loadPhoto(photoId).then((b) => b && setPhoto((url = URL.createObjectURL(b))));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [photoId]);
  const csv = () =>
    toCsv(
      [
        { header: "Client", value: (s: (typeof story.stays)[number]) => catalog.client(s.clientId)?.name ?? s.clientId },
        { header: "Spot", value: (s) => catalog.slot(s.slotId).label },
        { header: "Placed on", value: (s) => s.placedOn },
        { header: "Collected on", value: (s) => s.collectedOn ?? "Still there" },
        { header: "Days", value: (s) => s.days },
      ],
      story.stays,
    );
  const standing = history.whereIs(potId);
  return (
    <section className="rtab" aria-label={`Pot ${potId}`}>
      <div className="pothead">
        <span className="pothead__num">{potId}</span>
        <span>
          {potLabel(catalog, potId)}
          <small>
            {story.writeOff
              ? `Written off ${formatDay(story.writeOff.on)}`
              : standing
                ? `At ${where(catalog, standing.clientId, standing.slotId)}`
                : "In stock"}
            {pot?.asset?.costCents !== undefined ? ` · ${money(pot.asset.costCents)}` : ""}
            {pot?.asset?.boughtOn ? ` · bought ${formatDay(pot.asset.boughtOn)}` : ""}
          </small>
        </span>
      </div>
      <div className="figures">
        <Figure value={story.stays.length} label="placements" />
        <Figure value={story.daysOut} label="days at clients" />
        <Figure value={story.refusals.length} label="refusals" />
      </div>
      {story.writeOff ? (
        <div className="writeoff">
          <p className="writeoff__title">Written off · {WRITE_OFF_LABEL[story.writeOff.reason]}</p>
          <p>
            {formatDay(story.writeOff.on)}
            {story.writeOff.at ? ` · was at ${where(catalog, story.writeOff.at.clientId, story.writeOff.at.slotId)}` : ""}
          </p>
          {story.writeOff.note && <p>"{story.writeOff.note}"</p>}
          {photo && <img className="writeoff__photo" src={photo} alt={`Write-off photo of ${potId}`} />}
        </div>
      ) : (
        pot && (
          <button className="btn btn--ghost btn--block" onClick={props.onWriteOff}>
            Write off <span className="inline-pot">{potId}</span>…
          </button>
        )
      )}
      <ExportButton onClick={() => props.onExport(`pot ${potId} history`, csv())} />
      <h3 className="rtab__sub">Where it has been</h3>
      {story.stays.length === 0 && <p className="rtab__empty">Never placed yet.</p>}
      <ul className="rlist">
        {story.stays.map((s) => (
          <li key={s.eventId} className="rrow rrow--static">
            <span className="rrow__main rrow__main--wide">
              {catalog.client(s.clientId)?.name}, {catalog.slot(s.slotId).label}
              <small>
                {formatDay(s.placedOn)} – {s.collectedOn ? formatDay(s.collectedOn) : "still there"}
              </small>
            </span>
            <span className="rrow__side">{s.days} days</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Stock health ─────────────────────────────────────────────────────────── */

const LOW = 3;

function StockTab(props: { catalog: Catalog; history: History; assessor: Assessor; today: CalendarDate; onExport: (n: string, csv: string) => void }) {
  const { catalog, history, assessor, today } = props;
  const rows = useMemo(() => headroom(catalog, history, assessor, today), [catalog, history, assessor, today]);
  const idle = useMemo(() => idleStock(catalog, history, today), [catalog, history, today]);
  const csv = () =>
    toCsv(
      [
        { header: "Client", value: (r: (typeof rows)[number]) => catalog.client(r.clientId)?.name ?? r.clientId },
        { header: "Eligible pots", value: (r) => r.eligible },
        { header: "Good fit", value: (r) => r.goodFit },
        { header: "In circulation", value: (r) => r.inCirculation },
        { header: "Next frees up", value: (r) => (r.nextFreesUp ? `${r.nextFreesUp.potId} on ${r.nextFreesUp.on}` : "") },
      ],
      rows,
    );
  return (
    <section className="rtab" aria-label="Stock health">
      <p className="rtab__intro">How many pots each client may still have under the 12-month promise. Fewest first.</p>
      <ExportButton onClick={() => props.onExport("stock health", csv())} />
      <ul className="rlist">
        {rows.map((r) => (
          <li key={r.clientId} className="rrow rrow--static">
            <span className="rrow__main rrow__main--wide">
              {catalog.client(r.clientId)?.name}
              <small>
                {r.goodFit} also meet their requirements
                {r.nextFreesUp && (
                  <>
                    {" · next frees up "}
                    <span className="nowrap">{formatDay(r.nextFreesUp.on)}</span>
                  </>
                )}
              </small>
            </span>
            <span className={`rrow__count${r.eligible <= LOW ? " is-low" : ""}`}>
              {r.eligible <= LOW && <TriangleAlert aria-hidden size={14} />}
              <b>{r.eligible}</b> left
            </span>
          </li>
        ))}
      </ul>
      <h3 className="rtab__sub">Sitting at home</h3>
      {idle.length === 0 && <p className="rtab__empty">Every pot has been out in the last 60 days.</p>}
      <ul className="rlist">
        {idle.map((i) => (
          <li key={i.potId} className="rrow rrow--static">
            <span className="rrow__pot">{i.potId}</span>
            <span className="rrow__main">
              {potLabel(catalog, i.potId)}
              <small>{i.since ? `Home since ${formatDay(i.since)}` : "Never been out"}</small>
            </span>
            <span className="rrow__side">{i.days !== null ? `${i.days} days` : "New"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Month ────────────────────────────────────────────────────────────────── */

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function MonthTab(props: { catalog: Catalog; history: History; month: string; setMonth: (m: string) => void; onExport: (n: string, csv: string) => void }) {
  const { catalog, history, month } = props;
  const m = useMemo(() => monthSummary(history, month), [history, month]);
  const [y, mm] = month.split("-").map(Number) as [number, number];
  const title = `${MONTH_NAMES[mm - 1]} ${y}`;
  const shift = (n: number) => props.setMonth(addMonths(`${month}-01` as CalendarDate, n).slice(0, 7));
  type Line = { type: string; on: string; pot: string; client: string; detail: string };
  const lines: Line[] = [
    ...m.overrides.map((o) => ({ type: "Override", on: o.placedOn, pot: o.potId, client: catalog.client(o.clientId)?.name ?? o.clientId, detail: o.override?.reason ?? "" })),
    ...history.rejections.filter((r) => r.on.startsWith(month)).map((r) => ({ type: "Refusal", on: r.on, pot: r.potId, client: catalog.client(r.clientId)?.name ?? r.clientId, detail: r.reason ?? "" })),
    ...m.writeOffs.map((w) => ({ type: "Write-off", on: w.on, pot: w.potId, client: w.at ? (catalog.client(w.at.clientId)?.name ?? "") : "", detail: `${WRITE_OFF_LABEL[w.reason]}${w.note ? `: ${w.note}` : ""}` })),
  ];
  const csv = () =>
    toCsv(
      [
        { header: "Type", value: (l: Line) => l.type },
        { header: "Date", value: (l) => l.on },
        { header: "Pot", value: (l) => l.pot },
        { header: "Client", value: (l) => l.client },
        { header: "Detail", value: (l) => l.detail },
      ],
      lines,
    );
  return (
    <section className="rtab" aria-label={title}>
      <div className="switcher">
        <button aria-label="Previous month" onClick={() => shift(-1)}>
          <ChevronLeft aria-hidden size={20} />
        </button>
        <span>
          <b>{title}</b>
        </span>
        <button aria-label="Next month" onClick={() => shift(1)}>
          <ChevronRight aria-hidden size={20} />
        </button>
      </div>
      <div className="figures">
        <Figure value={m.placements} label="swaps" />
        <Figure value={m.clientsVisited} label="clients" />
        <Figure value={m.refusals.total} label="refusals" />
        <Figure value={m.overrides.length} label="overrides" tone={m.overrides.length ? "check" : undefined} />
        <Figure value={m.writeOffs.length} label="write-offs" />
      </div>
      <ExportButton onClick={() => props.onExport(`month ${month}`, csv())} />
      {lines.length === 0 && <p className="rtab__empty">No refusals, overrides or write-offs this month.</p>}
      {Object.keys(m.refusals.byReason).length > 0 && (
        <>
          <h3 className="rtab__sub">Why clients refused</h3>
          <ul className="rlist">
            {Object.entries(m.refusals.byReason).map(([reason, n]) => (
              <li key={reason} className="rrow rrow--static">
                <span className="rrow__main rrow__main--wide">{reason}</span>
                <span className="rrow__side">{n}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {lines.filter((l) => l.type !== "Refusal").length > 0 && (
        <>
          <h3 className="rtab__sub">Overrides and write-offs</h3>
          <ul className="rlist">
            {lines
              .filter((l) => l.type !== "Refusal")
              .map((l, i) => (
                <li key={`${l.type}-${l.pot}-${i}`} className="rrow rrow--static">
                  <span className="rrow__pot">{l.pot}</span>
                  <span className="rrow__main">
                    {l.type}: {l.detail}
                    <small>
                      {formatDay(l.on as CalendarDate)}
                      {l.client ? ` · ${l.client}` : ""}
                    </small>
                  </span>
                </li>
              ))}
          </ul>
        </>
      )}
    </section>
  );
}
